/**
 * NEX-1168 (plan 2026-10-20 Tarea 2.7, Johan's answer 6) — the address search behind "Mis
 * depósitos": Google's suggestions for what the depósito types, and the address and coordinates of
 * the one it picks. The coordinates travel with the location (`POST`/`PATCH /storage/locations`'s
 * optional `latitude`/`longitude`/`placeId`) and are what the carrier's app navigates to.
 *
 * The same Google services, with the same options, as nexvia-web-app's address field
 * (`src/ui-component/extended/LocationAutocomplete.jsx` through `use-places-autocomplete`): Places
 * `AutocompleteService` for the suggestions (Uruguay only, in Spanish) and `Geocoder` by place id for
 * the formatted address and its coordinates — so the same key works for both apps. Without that
 * repo's two packages (`@react-google-maps/api`, `use-places-autocomplete`): this panel loads the
 * Maps JavaScript API itself, with one `<script>`, once, the first time the depósito types an address.
 * Left out on purpose (YAGNI): the web app's street-corner fallback ("Av. Italia y Propios" through
 * the Geocoder) and its neighborhood lookup — a depósito's address is a street and a number, and the
 * panel stores no neighborhood.
 *
 * No `@/env` here: the key arrives as an argument (`src/shared/google-maps-options.ts` reads it), and
 * nothing in this file talks to nexvia-api-web.
 */

/** What the field lists: Google's suggestion, as the design system's `Autocomplete` takes it. */
export interface AddressSuggestion {
  /** Google's place id. */
  id: string;
  label: string;
  secondary?: string;
}

/** A picked suggestion, resolved: the address to store and where it is. */
export interface ResolvedAddress {
  address: string;
  latitude: number;
  longitude: number;
  placeId: string | null;
}

/** The provider `LocationForm` receives (injected, like `onUploadPhoto`, so a test passes a fake). */
export interface AddressSearch {
  /** Suggestions for `query`; `[]` for a short query or when Google answers nothing (or fails). */
  suggest(query: string): Promise<AddressSuggestion[]>;
  /** The address and coordinates of a suggestion; `null` when Google cannot resolve it. */
  resolve(placeId: string): Promise<ResolvedAddress | null>;
}

/** The web app asks from the third character on (`getTransformedQuery`'s own threshold). */
export const MIN_ADDRESS_QUERY_LENGTH = 3;

// --- The slice of the Maps JavaScript API this file uses (no `@types/google.maps` installed) ------

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: { main_text?: string; secondary_text?: string };
}

interface GeocoderResult {
  formatted_address: string;
  place_id?: string;
  geometry: { location: { lat(): number; lng(): number } };
}

interface GoogleMapsApi {
  maps: {
    places: {
      AutocompleteService: new () => {
        getPlacePredictions(request: {
          input: string;
          componentRestrictions: { country: string };
          language: string;
        }): Promise<{ predictions: PlacePrediction[] | null }>;
      };
    };
    Geocoder: new () => {
      geocode(request: { placeId: string; language: string }): Promise<{ results: GeocoderResult[] | null }>;
    };
  };
}

/** Same region and language as nexvia-web-app's `config/google-maps.js`. */
const REGION = 'UY';
const LANGUAGE = 'es';
const LOADED_CALLBACK = '__nexviaGoogleMapsLoaded';

type GoogleWindow = Window & { google?: GoogleMapsApi; [LOADED_CALLBACK]?: () => void };

let pending: Promise<GoogleMapsApi> | null = null;

/**
 * Loads the Maps JavaScript API with the Places library, once per page: a second call while the
 * first is in flight, or after it landed, gets the same promise. A failed load (no network, a key
 * Google refuses) forgets the attempt, so the next keystroke tries again.
 */
export function loadGoogleMaps(apiKey: string): Promise<GoogleMapsApi> {
  const win = window as GoogleWindow;
  if (win.google?.maps?.places) return Promise.resolve(win.google);
  if (pending) return pending;

  pending = new Promise<GoogleMapsApi>((resolve, reject) => {
    const script = document.createElement('script');
    const fail = () => {
      pending = null;
      script.remove();
      reject(new Error('Google Maps JavaScript API did not load'));
    };
    win[LOADED_CALLBACK] = () => {
      if (win.google?.maps?.places) resolve(win.google);
      else fail();
    };
    const params = new URLSearchParams({
      key: apiKey,
      libraries: 'places',
      language: LANGUAGE,
      region: REGION,
      loading: 'async',
      callback: LOADED_CALLBACK
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return pending;
}

function toSuggestion(prediction: PlacePrediction): AddressSuggestion {
  const main = prediction.structured_formatting?.main_text;
  return {
    id: prediction.place_id,
    label: main || prediction.description,
    secondary: main ? prediction.structured_formatting?.secondary_text : undefined
  };
}

/**
 * The Google-backed `AddressSearch`. `load` is the loader (a test passes a fake API); every Google
 * failure reads as "nothing found", so the field degrades to typing the address by hand.
 */
export function createGooglePlacesSearch(
  apiKey: string,
  load: () => Promise<GoogleMapsApi> = () => loadGoogleMaps(apiKey)
): AddressSearch {
  return {
    async suggest(query) {
      const input = query.trim();
      if (input.length < MIN_ADDRESS_QUERY_LENGTH) return [];
      try {
        const google = await load();
        const { predictions } = await new google.maps.places.AutocompleteService().getPlacePredictions({
          input,
          componentRestrictions: { country: REGION },
          language: LANGUAGE
        });
        return (predictions ?? []).map(toSuggestion);
      } catch {
        return [];
      }
    },

    async resolve(placeId) {
      try {
        const google = await load();
        const { results } = await new google.maps.Geocoder().geocode({ placeId, language: LANGUAGE });
        const first = results?.[0];
        if (!first) return null;
        const latitude = first.geometry.location.lat();
        const longitude = first.geometry.location.lng();
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
        return { address: first.formatted_address, latitude, longitude, placeId: first.place_id ?? placeId };
      } catch {
        return null;
      }
    }
  };
}
